import React, { useEffect, useState, useCallback, useRef } from 'react'
import { message, Table, Tag, Drawer, Form, Select, Spin, DatePicker, Input, InputNumber, Checkbox, Button, Tooltip } from 'antd'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FaEye, FaQrcode, FaEdit, FaDownload, FaPlus } from 'react-icons/fa'
import { RiCloseLine, RiDeleteBin6Line } from 'react-icons/ri'
import { fetchAllOrganization, verifyToken } from '../../utils/helpers'
import { secureApi } from '../../Config/api'
import { GET, POST } from '../../utils/apis'
import { pageSizeOptions, translations, dateFormate } from '../../utils/constants'
import { Search, ParkingCircle, X, LogIn, CreditCard, LogOut, Building2, Cctv } from 'lucide-react'
import moment from 'moment'
import { DeleteModal, SearchOrganizationsParking } from '../../Components'
import { SearchOutlined } from '@ant-design/icons'
import { setOrganizationFilter } from '../../Redux/actions/organizationActions'
import { setUserFilter } from '../../Redux/actions/filterUsersAction'
import socket from '../../Config/socket'

const CameraMapings = ({ language }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector(state => state?.authReducer?.user)
  const organizationDetails = useSelector(state => state?.authReducer?.organization)
  const organizationFilter = useSelector(state => state.organizationReducer.organizationFilter)
  const userFilterRedux = useSelector(state => state?.filterUsersReducer?.userFilter)
  const organizationCount = useSelector(state => state.organizationReducer?.orgSelectionCount || 0)
  const organizationFromRedux = useSelector((state) => state.organizationReducer?.allOrganizations)

  const [editForm] = Form.useForm()
  const [addForm] = Form.useForm()

  const [initialLoading, setInitialLoading] = useState(false)
  const [parkingData, setParkingData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [viewDrawer, setViewDrawer] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [spinLoading, setSpinLoading] = useState({ active: false, id: null })
  const [filterStatus, setFilterStatus] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [searchTerm, setSearchTerm] = useState('');
  const [deviceIdFilter, setDeviceIdFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [dateRange, setDateRange] = useState([])
  const [statsData, setStatsData] = useState({ entered: 0, paid: 0, exited: 0 })
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteRecord, setSelectedDeleteRecord] = useState(null);
  const [searchModal, setSearchModal] = useState(false)
  const [organizations, setOrganizations] = useState([])

  const searchTimerRef = useRef(null)
  // Always points at the latest fetchCameraMappings so the socket listener
  // (registered once) re-fetches with current page/filters.
  const fetchDataRef = useRef(() => { })

  const { RangePicker } = DatePicker

  const organizationId = organizationDetails?.id

  useEffect(() => {
    verifyToken(history, dispatch)
    fetchAllOrganization({ dispatch })
    fetchCameraMappings()
    setOrganizations(organizationFromRedux || [])
  }, [])

  useEffect(() => {
    setOrganizations(organizationFromRedux || [])
  }, [organizationFromRedux])

  useEffect(() => {
    fetchCameraMappings()
  }, [currentPage, pageSize, filterStatus, dateRange, searchTerm, deviceIdFilter, isActiveFilter, organizationFilter])

  // Live updates: refresh the list whenever a camera mapping is added/updated/
  // deleted on the server (including auto-added from camera events).
  useEffect(() => {
    const handleLiveUpdate = () => {
      fetchDataRef.current?.()
    }

    socket.on('camera_mapping_updated', handleLiveUpdate)

    return () => {
      socket.off('camera_mapping_updated', handleLiveUpdate)
    }
  }, [])

  // useEffect(() => {
  //   if (parkingData && parkingData.length > 0) {
  //     const entered = parkingData.filter(item => item.status === 'entered').length
  //     const paid = parkingData.filter(item => item.status === 'paid').length
  //     const exited = parkingData.filter(item => item.status === 'exited').length
  //     setStatsData({ entered, paid, exited })
  //   } else {
  //     setStatsData({ entered: 0, paid: 0, exited: 0 })
  //   }
  // }, [parkingData])

  const fetchCameraMappings = async () => {
    try {
      setInitialLoading(true);

      const params = {
        page: currentPage,
        limit: pageSize,
        role: user?.role,
        organization_id: organizationId,
        organization_filter: organizationFilter,
      };

      // Add filters if they have values
      if (searchTerm?.trim()) params.search = searchTerm.trim();
      if (isActiveFilter) params.is_active = isActiveFilter;
      if (dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await secureApi.get(GET?.GET_CAMERA_MAPPING, { params });

      if (response?.success) {
        const records = response?.data?.records || [];
        const pagination = response?.data?.pagination || {};

        setParkingData(records);
        setFilteredData(records);
        setTotalItems(pagination?.totalRecords || records.length);
      } else {
        message.error(response?.message || translations[language]?.fetchCameraMappingError || 'Failed to fetch camera mapping data.');
        setParkingData([]);
        setFilteredData([]);
        setTotalItems(0);
      }

      setInitialLoading(false);
    } catch (error) {
      console.error('Error fetching Camera Mappings:', error);
      message.error(translations[language]?.errorFetchingCameraMappings || 'Error fetching Camera Mappings.');
      setParkingData([]);
      setFilteredData([]);
      setTotalItems(0);
      setInitialLoading(false);
    }
  };

  // Keep the ref in sync so socket-triggered refreshes use current filters
  fetchDataRef.current = fetchCameraMappings

  // Debounced search handler - waits 500ms after user stops typing
  const handleSearch = useCallback((query) => {
    setSearchQuery(query)

    // Clear previous timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }

    // Set new timer for debounced API call
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1) // Reset to first page on new search
      fetchCameraMappings(query)
    }, 500) // 500ms debounce delay
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates)
    if (dates && dates.length === 2) {
      setStartDate(dates[0].format('YYYY-MM-DD'))
      setEndDate(dates[1].format('YYYY-MM-DD'))
    } else {
      setStartDate('')
      setEndDate('')
    }
    setCurrentPage(1)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('')
    setDateRange([])
    setStartDate('')
    setEndDate('')
    setFilterStatus('')
    setCurrentPage(1)
  }

  const viewDetails = (record) => {
    setSelectedRecord(record)
    setViewDrawer(true)
  }

  const closeViewDrawer = () => {
    setSelectedRecord(null)
    setViewDrawer(false)
  }

  const handleEdit = (record) => {
    setSelectedRecord(record)

    editForm.setFieldsValue({
      organization: record.organization_id || organizationId,
      camera_name: record.camera_name || '',
      device_id: record.device_id || '',
      channel: record.channel ?? '',
      lane_no: record.lane_no || '',
      parking_stall_no: record.parking_stall_no || '',
      gate_id: record.gate_id || '',
      direction: record.direction || '',
      location: record.location || '',
      description: record.description || '',
    })

    setViewDrawer(false)
    setEditModal(true)
  }

  const showAddModal = async () => {
    const isTokenValid = await verifyToken(history, dispatch)
    if (!isTokenValid) {
      message.error(translations[language]?.sessionExpired || 'Session expired. Please log in again.')
      return
    }

    addForm.resetFields()
    if (organizationId) {
      addForm.setFieldsValue({ organization: organizationId })
    }
    setAddModal(true)
  }

  const closeAddModal = () => {
    addForm.resetFields()
    setAddModal(false)
  }

  const closeEditModal = () => {
    setSelectedRecord(null)
    setEditModal(false)
    editForm.resetFields()
  }

  const handleOrganizationChange = (value) => {
    dispatch(setOrganizationFilter(value))
    dispatch(setUserFilter(value))
  }

  const showSearchModal = () => {
    setSearchModal(!searchModal)
  }

  const getCameraMappingPayload = (values) => {
    const organizationIdToSend = user?.role === 'superAdmin'
      ? values?.organization
      : organizationId

    return {
      camera_name: values?.camera_name || values?.camera_id,
      device_id: values?.device_id,
      channel: values?.channel ?? values?.channel_no,
      lane_no: values?.lane_no || values?.lane_name,
      parking_stall_no: values?.parking_stall_no,
      gate_id: values?.gate_id,
      direction: values?.direction || values?.gate_type,
      location: values?.location,
      description: values?.description,
      is_active: values?.is_active,
      organization_id: organizationIdToSend
    }
  }

  const handleAddCameraMapping = async () => {
    const isTokenValid = await verifyToken(history, dispatch)
    if (!isTokenValid) {
      message.error(translations[language]?.sessionExpired || 'Session expired. Please log in again.')
      return
    }

    try {
      await addForm.validateFields()
      setAddLoading(true)

      const values = addForm.getFieldsValue()
      const payload = getCameraMappingPayload(values)

      if (!payload?.organization_id) {
        message.error(translations[language]?.organizationRequired || 'Organization is required.')
        setAddLoading(false)
        return
      }

      const response = await secureApi.post(POST?.ADD_CAMERA_MAPPING, payload)

      if (response?.success) {
        message.success(translations[language]?.cameraMappingAdded || 'Camera Mapping added successfully')
        fetchCameraMappings()
        closeAddModal()
      } else {
        message.error(response?.message || translations[language]?.failedToAddCameraMapping || 'Failed to add Camera Mapping.')
      }
      setAddLoading(false)
    } catch (error) {
      console.error('Error adding Camera Mapping:', error)
      message.error(translations[language]?.errorAddingCameraMapping || 'Error adding Camera Mapping.')
      setAddLoading(false)
    }
  }

  const handleUpdateSession = async () => {
    const isTokenValid = await verifyToken(history, dispatch)
    if (!isTokenValid) {
      message.error(translations[language]?.sessionExpired || 'Session expired. Please log in again.')
      return
    }

    try {
      await editForm.validateFields()
      setEditLoading(true)

      const values = editForm.getFieldsValue()
      const payload = getCameraMappingPayload(values)

      if (!payload?.organization_id) {
        message.error(translations[language]?.organizationRequired || 'Organization is required.')
        setEditLoading(false)
        return
      }

      const response = await secureApi.post(POST?.UPDATE_CAMERA_MAPPING, {
        id: selectedRecord.id,
        ...payload
      })

      if (response?.success) {
        message.success(translations[language]?.cameraMappingUpdated || 'Camera Mapping updated successfully')
        fetchCameraMappings()
        closeEditModal()
      } else {
        message.error(response?.message || translations[language]?.failedToUpdateCameraMapping || 'Failed to update Camera Mapping.')
      }
      setEditLoading(false)
    } catch (error) {
      console.error('Error updating Camera Mapping:', error)
      message.error(translations[language]?.errorUpdatingCameraMapping || 'Error updating Camera Mapping.')
      setEditLoading(false)
    }
  }

  const handleActive = async (is_active, id) => {
    const isTokenValid = await verifyToken(history, dispatch)
    if (!isTokenValid) {
      message.error(translations[language]?.sessionExpired || 'Session expired. Please log in again.')
      return
    }

    try {
      setEditLoading(true)

      const values = editForm.getFieldsValue()

      const response = await secureApi.post(POST?.UPDATE_CAMERA_MAPPING, {
        id,
        is_active
      })

      if (response?.success) {
        message.success(translations[language]?.cameraMappingUpdated || 'Camera Mapping updated successfully')
        fetchCameraMappings()
      } else {
        message.error(response?.message || translations[language]?.failedToUpdateCameraMapping || 'Failed to update Camera Mapping.')
      }
      setEditLoading(false)
    } catch (error) {
      console.error('Error updating Camera Mapping:', error)
      message.error(translations[language]?.errorUpdatingCameraMapping || 'Error updating Camera Mapping.')
      setEditLoading(false)
    }
  }
  const handleDeleteCancel = () => {
    setDeleteModal(false);
    setSelectedDeleteRecord(null);
  };

  const handleDelete = async () => {
    const isTokenValid = await verifyToken(history, dispatch)
    if (!isTokenValid) {
      message.error(translations[language]?.sessionExpired || 'Session expired. Please log in again.')
      return
    }

    try {
      setEditLoading(true)

      const values = editForm.getFieldsValue()

      const response = await secureApi.post(POST?.DELETE_CAMERA_MAPPING, { id: selectedDeleteRecord })

      if (response?.success) {
        message.success(translations[language]?.cameraMappingDeleted || 'Camera mapping deleted successfully')
        setDeleteModal(false)
        fetchCameraMappings()
      } else {
        message.error(response?.message || translations[language]?.failedToDeleteCameraMapping || 'Failed to delete camera mapping.')
      }
      setEditLoading(false)
      setDeleteModal(false)
    } catch (error) {
      console.error('Error deleting camera mapping:', error)
      message.error(translations[language]?.errorDeletingCameraMapping || 'Error deleting camera mapping.')
      setEditLoading(false)
      setDeleteModal(false)
    }
  }

  const statusOptions = [
    { label: translations[language]?.statusEntered || 'Entered', value: 'entered' },
    { label: translations[language]?.statusPaid || 'Paid', value: 'paid' },
    { label: translations[language]?.statusExited || 'Exited', value: 'exited' }
  ]

  const gateTypeOptions = [
    { label: translations[language]?.entry || 'Entry', value: 'entry' },
    { label: translations[language]?.exit || 'Exit', value: 'exit' },
  ]

  const getStatusColor = (status) => {
    switch (status) {
      case 'exited':
        return 'green'
      case 'entered':
        return 'blue'
      case 'paid':
        return 'cyan'
      default:
        return 'default'
    }
  }

  const getStatusText = (status) => {
    const statusMap = {
      exited: translations[language]?.statusExited || 'Exited',
      entered: translations[language]?.statusEntered || 'Entered',
      paid: translations[language]?.statusPaid || 'Paid'
    }
    return statusMap[status] || status
  }

  // Open QR PDF in new tab
  const openQRCode = (url) => {
    if (url) {
      window.open(url, '_blank')
    } else {
      message.warning(translations[language]?.qrCodeNotAvailable || 'QR code not available')
    }
  }

  const downloadPDF = async (url, sessionCode) => {
    if (!url) {
      message.warning(translations[language]?.pdfNotAvailable || 'PDF not available')
      return
    }
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `${sessionCode || 'parking'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
      message.success(translations[language]?.downloadSuccess || 'Downloaded successfully')
    } catch (error) {
      console.error('Download error:', error)
      message.error(translations[language]?.downloadError || 'Failed to download')
    }
  }

  const columns = [
    {
      title: translations[language]?.cameraName || 'Camera Name',
      dataIndex: 'camera_name',
      key: 'camera_name',
      render: (text) => (
        <span className='vl-name-cell' style={{ cursor: 'default', minWidth: 0 }}>
          <span className='vl-avatar vl-avatar-icon'><Cctv size={15} /></span>
          <span className='vl-name-text'>{text || <span className='vl-muted'>-</span>}</span>
        </span>
      )
    },
    {
      title: translations[language]?.channelNo || 'Channel no',
      dataIndex: 'channel',
      key: 'channel',
      render: (text) => (text === null || text === undefined || text === '') ? <span className='vl-muted'>-</span> : <span className='vl-plate'>{text}</span>
    },
    {
      title: translations[language]?.gateId || 'Gate id',
      dataIndex: 'gate_id',
      key: 'gate_id',
      width: 150,
      render: (text) => text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>
    },
    {
      title: translations[language]?.direction || 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      width: 150,
      render: (text) => {
        if (!text) return <span className='vl-muted'>-</span>
        const dir = String(text).toLowerCase()
        const tone = dir.includes('entr') || dir.includes('in')
          ? 'dash-status-ok'
          : dir.includes('exit') || dir.includes('out') || dir.includes('usc')
            ? 'dash-status-info'
            : 'dash-status-ko'
        return <span className={`dash-status-pill ${tone} capitalized-name`}><span className='dash-status-dot' />{text}</span>
      }
    },
    {
      title: translations[language]?.location || 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      render: (text) => text ? <span style={{ whiteSpace: 'nowrap' }}>{text}</span> : <span className='vl-muted'>-</span>
    },
    // {0000
    //   title: translations[language]?.parkingStallNo || 'Parking Stall No',
    //   dataIndex: 'parking_stall_no',
    //   key: 'parking_stall_no',
    //   width: 150,
    //   align: 'center',
    //   render: (text) => <span style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '12px' }}>{text || '-'}</span>
    // },
    {
      title: translations[language]?.laneName || 'lane',
      dataIndex: 'lane_no',
      key: 'lane_no',
      width: 110,
      align: 'center',
      render: (text) => (text === null || text === undefined || text === '') ? <span className='vl-muted'>-</span> : <span className='vl-count-chip'>{text}</span>
    },
    ...(user?.role === 'superAdmin' ? [{
      title: translations[language]?.organization || 'Organization',
      dataIndex: 'organization_name',
      key: 'organization_name',
      width: 150,
      render: (text) => {
        if (!text) return <span className='vl-muted'>-</span>
        const isLong = text.length > 20
        const display = isLong ? `${text.slice(0, 20)}...` : text
        return (
          <Tooltip title={isLong ? text : ''}>
            <span className='capitalized-name' style={{ whiteSpace: 'nowrap' }}>{display}</span>
          </Tooltip>
        )
      }
    }] : []),
    {
      title: translations[language]?.description || 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 220,
      ellipsis: true,
      render: (text) => text ? <span className='vl-secondary' title={text}>{text}</span> : <span className='vl-muted'>-</span>
    },
    {
      title: translations[language]?.action || 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div className='vl-actions'>
          <Tooltip title={translations[language]?.edit || 'Edit'}>
            <button
              type='button'
              className='vl-action-btn vl-action-edit'
              onClick={() => handleEdit(record)}
            >
              <FaEdit size={14} />
            </button>
          </Tooltip>
          <Tooltip title={translations[language]?.delete || 'Delete'}>
            <button
              type='button'
              className='vl-action-btn vl-action-delete'
              onClick={() => { setSelectedDeleteRecord(record.id); setDeleteModal(true) }}
            >
              <RiDeleteBin6Line size={14} />
            </button>
          </Tooltip>
        </div>
      )
    }
  ]

  const renderCameraMappingFields = (isEdit = false) => (
    <>
      {user?.role === 'superAdmin' && (
        <Form.Item
          name="organization"
          label={translations[language]?.organizations || 'Organization'}
          rules={[{ required: true, message: translations[language]?.organizationSelect || 'Select organization' }]}
        >
          <Select
            showSearch
            placeholder={translations[language]?.organizationSelect || 'Select organization'}
            optionFilterProp="children"
            className='custom-select'
            dropdownClassName='custom-select-dropdown'
          >
            {organizations?.map(org => (
              <Select.Option key={org.id} value={org.id}>
                {org.organization_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item
        name="camera_name"
        label={translations[language]?.cameraName || 'Camera Name'}
      >
        <Input className="inp-height-43" placeholder={translations[language]?.enterCameraName || 'Enter Camera Name'} />
      </Form.Item>

      <Form.Item
        name="device_id"
        label={translations[language]?.deviceId || 'Device Id'}
        rules={[{ required: true, message: translations[language]?.deviceIdRequired || 'Please enter device id' }]}
      >
        <Input className="inp-height-43" disabled={isEdit} placeholder={translations[language]?.enterDeviceId || 'Enter Device Id'} />
      </Form.Item>

      <Form.Item
        name="channel"
        label={translations[language]?.channelNo || 'Channel No'}
        rules={[{ required: true, message: translations[language]?.channelNoRequired || 'Please enter channel number' }]}
      >
        <Input className="inp-height-43" placeholder={translations[language]?.enterChannelNo || 'Enter Channel No'} />
      </Form.Item>

      <Form.Item
        name="lane_no"
        label={translations[language]?.laneName || 'Lane No'}
      >
        <Input className="inp-height-43" placeholder={translations[language]?.enterLane || 'Enter Lane'} />
      </Form.Item>

      {/* <Form.Item
        name="parking_stall_no"
        label={translations[language]?.parkingStallNo || 'Parking Stall No'}
      >
        <Input placeholder={translations[language]?.enterParkingStall || 'Enter Parking Stall No'} />
      </Form.Item> */}

      <Form.Item
        name="gate_id"
        label={translations[language]?.gateId || 'Gate Id'}
      >
        <Input className="inp-height-43" placeholder={translations[language]?.enterGateId || 'Enter Gate Id'} />
      </Form.Item>

      <Form.Item
        name="direction"
        label={translations[language]?.direction || 'Direction'}
        rules={[{ required: true, message: translations[language]?.directionRequired || 'Please enter direction' }]}
      >
        <Input className="inp-height-43" placeholder={translations[language]?.enterDirection || 'Enter Direction (entry, exit, parking)'} />
      </Form.Item>

      <Form.Item
        name="location"
        label={translations[language]?.location || 'Location'}
      >
        <Input className="inp-height-43" placeholder={translations[language]?.enterLocation || 'Enter Location'} />
      </Form.Item>

      <Form.Item
        name="description"
        label={translations[language]?.description || 'Description'}
      >
        <Input.TextArea rows={3} style={{ maxHeight: 200, overflowY: 'auto' }} placeholder={translations[language]?.enterDescription || 'Enter description'} />
      </Form.Item>
    </>
  )

  return (
    <div className='main-container'>
      <div className='main-inner-container'>
        <div className='insider-container-buttons report-header-div'>
          <h1 className='main-heading'>{translations[language]?.cameraMappings || 'Camera Mappings'}</h1>
        </div>
        <div className='camera-mapping-toolbar'>
          <div className='camera-mapping-add'>
            {(user?.role === 'admin' || user?.role === 'superAdmin') && (
              <button
                className='add-button'
                onClick={showAddModal}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  width: 'auto',
                  minWidth: 'fit-content',
                  flexShrink: 0,
                }}
              >
                <FaPlus className='icons' />
                {translations[language]?.addCameraMapping || 'Add Camera Mapping'}
              </button>
            )}
          </div>

          <div className='camera-mapping-controls'>
            {/* Search with icon */}
            <div className='camera-mapping-control camera-mapping-search'>
              <Input
                placeholder={translations[language]?.search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', height: 40, borderRadius: 6, borderWidth: 1, borderStyle: 'solid', borderColor: '#d9d9d9', boxSizing: 'border-box' }}
                prefix={<SearchOutlined />}
                allowClear
              />
            </div>

            {/* Is Active */}
            <div className='camera-mapping-control camera-mapping-filter'>
              <Select
                value={isActiveFilter}
                onChange={(value) => setIsActiveFilter(value)}
                className='is-active-select'
                style={{ width: '100%', height: '40px !important', borderRadius: 6, borderColor: '#d9d9d9', boxSizing: 'border-box' }}
                placeholder={translations[language]?.isActive || 'Is Active'}
              >
                <Select.Option value="">{translations[language]?.all || 'All'}</Select.Option>
                <Select.Option value="true">{translations[language]?.active || 'Active'}</Select.Option>
                <Select.Option value="false">{translations[language]?.inactive || 'Inactive'}</Select.Option>
              </Select>
            </div>

            <div className='camera-mapping-control camera-mapping-date'>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                style={{ width: '100%', height: 40, borderRadius: 6, borderWidth: 1, borderStyle: 'solid', borderColor: '#d9d9d9', boxSizing: 'border-box' }}
                format="YYYY-MM-DD"
                disabledDate={(current) => current && current > moment().endOf('day')}
                placeholder={[
                  translations[language]?.startDate || 'Start Date',
                  translations[language]?.endDate || 'End Date'
                ]}
                allowClear
              />
            </div>

            {user?.role === 'superAdmin' && (
              <div className='camera-mapping-control camera-mapping-org'>
                <span
                  className={`organization ${searchModal ? 'open-organization' : 'close-organization'}`}
                  onClick={showSearchModal}
                >
                  <Building2 />
                  {translations[language]?.organizations || 'Organizations'}

                  {organizationCount ? (
                    <div className='filter-count-badge'>
                      {organizationCount}
                    </div>
                  ) : null}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className='insider-container'>
          {/* <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '15px',
            marginBottom: '20px',
            alignItems: 'center'
          }}>
            <div style={{ flex: '1 1 300px', minWidth: '200px', maxWidth: '400px' }}>
              <div className="search-inner-div" style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '6px 12px',
                backgroundColor: '#fff'
              }}>
                <Search style={{ width: '18px', height: '18px', color: '#999', marginRight: '8px' }} />
                <input
                  placeholder={translations[language]?.searchParking || 'Search by plate number...'}
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    flex: 1,
                    fontSize: '14px',
                    minWidth: '100px'
                  }}
                />
                {searchQuery && (
                  <span style={{ cursor: 'pointer', color: '#999', fontSize: '14px' }} onClick={() => handleSearch('')}>
                    ✕
                  </span>
                )}
              </div>
            </div>

            <div style={{ flex: '0 1 auto' }}>
              <RangePicker
                className='ip-address-date-picker'
                format={dateFormate}
                value={dateRange}
                onChange={handleDateRangeChange}
                disabledDate={(current) => current && current > moment().endOf('day')}
                placeholder={[
                  translations[language]?.startDate || 'Start Date',
                  translations[language]?.endDate || 'End Date'
                ]}
                style={{ width: '100%', minWidth: '250px' }}
                allowClear
              />
            </div>

            {(searchQuery || dateRange.length > 0 || filterStatus) && (
              <button
                onClick={clearFilters}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 16px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#666',
                  whiteSpace: 'nowrap',
                  height: '32px'
                }}
              >
                <X size={16} />
                {translations[language]?.clearFilters || 'Clear Filters'}
              </button>
            )}
          </div> */}

          <div className='vd-card'>
            {parkingData.length === 0 && !initialLoading ? (
              <div className='vd-card-body'>
                <div className="no-vehicles-container">
                  <div className="no-vehicles-text-center">
                    <div className="flex-col-center">
                      <div className="no-vehicles-text-center">
                        <ParkingCircle className='no-vehicle-icon' />
                        <h3 className="no-vehicles-heading">{translations[language]?.noParkingFound || 'No Parking Records Found'}</h3>
                        <p className="no-vehicles-description">
                          {translations[language]?.noParkingHistory || 'No parking history available yet'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className='vd-card-header'>
                  <div className='vl-list-title'>
                    <h2 className='vd-card-title'>{translations[language]?.cameraMappingList || 'Camera Mapping List'}</h2>
                    <span className='vl-total-chip'>{translations[language]?.totalRecord || 'Total Records'}: <strong>{totalItems}</strong></span>
                  </div>
                </div>
                <div className='vd-card-body'>
                  <Table
                    className='vl-table'
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: totalItems,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      pageSizeOptions,
                      onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      }
                    }}
                    scroll={{ x: 1100 }}
                    loading={initialLoading}
                    size="middle"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Drawer
        title={<span className="capitalized-name">{translations[language]?.parkingDetails || 'Parking Details'}</span>}
        visible={viewDrawer}
        onClose={closeViewDrawer}
        width={500}
        placement='right'
        closable={true}
        className='custom-drawer'
        closeIcon={<span className='custom-close-icon'><RiCloseLine /></span>}
        style={{ zIndex: 1000 }}
      >
        {selectedRecord && (
          <div className='custom-modal-inner-container'>
            <div className='information-container'>
              <h3 className='info-main-heading'>{translations[language]?.parkingDetails || 'Parking Details'}</h3>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.plateNumber || 'Plate Number'}</h3>
                    <p className='capitalized-name'>{selectedRecord.plate_number || '-'}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.sessionCode || 'Session Code'}</h3>
                    <p>{selectedRecord.session_code || '-'}</p>
                  </div>
                </div>
              </div>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.amount || 'Amount'}</h3>
                    <p>{selectedRecord.currency === 'EUR' ? '€' : selectedRecord.currency}{parseFloat(selectedRecord.payment_amount || 0).toFixed(2)}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.status || 'Status'}</h3>
                    <Tag color={getStatusColor(selectedRecord.status)} style={{ marginTop: '5px' }}>
                      {getStatusText(selectedRecord.status)}
                    </Tag>
                  </div>
                </div>
              </div>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.entryTime || 'Entry Time'}</h3>
                    <p>{selectedRecord.entry_time ? moment(selectedRecord.entry_time).format('DD/MM/YYYY HH:mm') : '-'}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.entryGate || 'Entry Gate'}</h3>
                    <p>{selectedRecord.entry_gate_id || '-'}</p>
                  </div>
                </div>
              </div>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.paymentTime || 'Payment Time'}</h3>
                    <p>{selectedRecord.payment_time ? moment(selectedRecord.payment_time).format('DD/MM/YYYY HH:mm') : '-'}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.paymentProvider || 'Payment Provider'}</h3>
                    <p>{selectedRecord.payment_provider || '-'}</p>
                  </div>
                </div>
              </div>

              <div className='inner-information-div'>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.exitTime || 'Exit Time'}</h3>
                    <p>{selectedRecord.exit_time ? moment(selectedRecord.exit_time).format('DD/MM/YYYY HH:mm') : '-'}</p>
                  </div>
                </div>
                <div className='info-childern-div'>
                  <div className='inner-information-childern'>
                    <h3 className='info-heading'>{translations[language]?.exitGate || 'Exit Gate'}</h3>
                    <p>{selectedRecord.exit_gate_id || '-'}</p>
                  </div>
                </div>
              </div>

              {selectedRecord.notes && (
                <div className='inner-information-div'>
                  <div className='info-childern-div' style={{ width: '100%' }}>
                    <div className='inner-information-childern'>
                      <h3 className='info-heading'>{translations[language]?.notes || 'Notes'}</h3>
                      <p>{selectedRecord.notes}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedRecord.qr_token && (
                <div className='drawer-action-buttons' style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className='add-button'
                    onClick={() => openQRCode(selectedRecord.qr_token)}
                  >
                    <FaQrcode style={{ marginRight: '8px' }} />
                    {translations[language]?.viewQRCode || 'View QR Code'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>

      {/* Add Modal */}
      <Drawer
        title={<span className="capitalized-name">{translations[language]?.addCameraMapping || 'Add Camera Mapping'}</span>}
        visible={addModal}
        onClose={closeAddModal}
        width={750}
        placement='right'
        closable={true}
        className='custom-drawer'
        closeIcon={<span className='custom-close-icon'><RiCloseLine /></span>}
        style={{ zIndex: 1001 }}
      >
        <Spin spinning={addLoading}>
          <Form form={addForm} layout="vertical">
            {renderCameraMappingFields(false)}

            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button key='cancel' onClick={closeAddModal} type='default' className='drawer-cancel'>
                {translations[language]?.cancelButton || 'Cancel'}
              </Button>
              <button
                type="button"
                className='add-button'
                onClick={handleAddCameraMapping}
                disabled={addLoading}
              >
                {addLoading ? (translations[language]?.adding || 'Adding...') : (translations[language]?.addCameraMapping || 'Add Camera Mapping')}
              </button>
            </div>
          </Form>
        </Spin>
      </Drawer>

      {/* Edit Modal */}
      <Drawer
        title={<span className="capitalized-name">{translations[language]?.editCameraMapping || 'Edit Camera Mapping'}</span>}
        visible={editModal}
        onClose={closeEditModal}
        width={750}
        placement='right'
        closable={true}
        className='custom-drawer'
        closeIcon={<span className='custom-close-icon'><RiCloseLine /></span>}
        style={{ zIndex: 1001 }}
      >
        <Spin spinning={editLoading}>
          <Form form={editForm} layout="vertical">
            {renderCameraMappingFields(true)}

            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button key='cancel' onClick={closeEditModal} type='default' className='drawer-cancel'>
                {translations[language]?.cancelButton || 'Cancel'}
              </Button>
              <button
                type="button"
                className='add-button'
                onClick={handleUpdateSession}
                disabled={editLoading}
              >
                {editLoading ? (translations[language]?.updating || 'Updating...') : (translations[language]?.updateCameraMapping || 'Update')}
              </button>
            </div>
          </Form>
        </Spin>
      </Drawer>
      <div className={`search-organization-main-div ${searchModal ? 'open' : ''}`}>
        <SearchOrganizationsParking
          searchType={"cameras"}
          selectedOrganizations={organizationFilter}
          handleOrganizationChange={handleOrganizationChange}
          organizations={organizations}
          setSearchModal={setSearchModal}
          searchModal={searchModal}
          selectedFilter={userFilterRedux}
          language={language}
        />
      </div>

      <DeleteModal
        title={translations[language]?.cameraMappings}
        bodyText={
          <>
            {translations[language]?.areYouSureYouWantToDltThisCameraList}
          </>
        }
        isOpen={deleteModal}
        onCancel={handleDeleteCancel}
        onDelete={handleDelete}
        language={language}
      />
    </div>
  )
}

export default CameraMapings
